/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { compose } from 'lodash/fp';
import { connect } from 'react-redux';
import * as classnames from 'classnames';
import * as _ from 'lodash';
import {
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';

import {
    QuestionFormWithOptionsBase,
    QuestionFormWithOptionsBaseProps,
    QuestionFormWithOptionsBaseStates
} from '../QuestionFormWithOptionsBase';
import {
    MULTIMEDIA_TYPES,
    OptionButton,
    OPTION_TYPES,
    PlainTextContent,
    QTYPES
} from '@nexxt/common/types';
import { TEXT_HIGHLIGHTER_OPTS } from '@nexxt/common/constants';
import NotifyHOC from '../../../../../../HOC/Notifications';
/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import QuestionSettings from '../../../common/questionSettings';
import { alertMessage } from '../../../../../../constants/alertMessage';
import CollaspeButton from '../../../common/optionCard/CollaspeButton';
import CheckboxMultiSelect from '../../../../../common/reactSelect/CheckboxMultiSelect';
import {
    DEFAULT_OPTION,
    DEFAULT_TRANSLATION_BUTTONS
} from '../../../../../../constants/defaultOption';
import { DEFAULT_TEXT } from '../../../../../../constants/defaultText';
import { Notes } from '../../../../../common/notes';
import MultimediaBubble from '../../../common/MultimediaBubble';

interface TextHighlighterProps extends QuestionFormWithOptionsBaseProps {
    // nothing special... yet!
    t?: (text: string, options?: Record<string, boolean>) => string; // translate function
}

interface TextHighlighterState extends QuestionFormWithOptionsBaseStates {
    // nothing special... yet!
}
class TextHighlighter extends QuestionFormWithOptionsBase<
    TextHighlighterProps,
    TextHighlighterState
> {
    constructor(p: QuestionFormWithOptionsBaseProps) {
        super(p, QTYPES.TEXT_HIGHLIGHTER);
        const { isNewQuestion } = this.props;
        const question = {
            ...this.state.question,
            showAlternateWording: true,
            showContinueButton: true,
            buttons: isNewQuestion
                ? DEFAULT_TRANSLATION_BUTTONS(
                      QTYPES.TEXT_HIGHLIGHTER,
                      this.props.t,
                      this.props.currentProject.languages.map((l) => l.value)
                  )
                : this.state.question.buttons
        };
        this.state = {
            ...this.state,
            question
        };
    }

    render() {
        const { currentProject, editEnabled } = this.props;
        const { WORD_DELIMITER, WORD_NUM_BREAKPOINTS, FONT_SIZES } =
            TEXT_HIGHLIGHTER_OPTS;
        const {
            question,
            isOptionExpand,
            isSettingBoxLayoutOpen,
            templateSettings
        } = this.state;
        const questionError =
            (this.state.showError && this.state.questionError) || {};
        const unlockSaveAndMedia = !editEnabled
            ? !!templateSettings || false
            : true;
        let lockMessage;
        if (templateSettings?.alertMessages?.length) {
            lockMessage = templateSettings?.alertMessages;
        } else {
            lockMessage = this.getWarningMessage();
        }

        // @TODO: make the font size Note work for i18n
        const numWords = (
            question.alternateWordingTexts[0]?.contents?.[0] as PlainTextContent
        )?.text?.split(WORD_DELIMITER)?.length;
        const dynamicFontSize =
            numWords > WORD_NUM_BREAKPOINTS[1]
                ? FONT_SIZES[0]
                : numWords > WORD_NUM_BREAKPOINTS[0]
                ? FONT_SIZES[1]
                : FONT_SIZES[2];
        return (
            <div>
                <div className="project-add--question-builder">
                    <BuilderHeader
                        toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                        isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                        showAlert={!this.props.editEnabled}
                        alertMessages={lockMessage}
                        question={question}
                        setValue={this.setQuestion}
                        disabled={!this.props.editEnabled}
                        errorObject={this.state.questionError}
                    />
                    <div className="builder-body">
                        {(isSettingBoxLayoutOpen && (
                            <QuestionSettings
                                onClose={this.toggleSettingBoxLayout}
                                question={question}
                                questionSettings={{
                                    allowUserEdit: true
                                }}
                                setValue={this.setQuestion}
                                disabled={!this.props.editEnabled}
                            />
                        )) ||
                            null}
                        {this.renderErrorNotes()}
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            addNewBubble={this.NEW_addNewEditor}
                            removeBubble={this.NEW_removeEditor}
                            disabled={!this.props.editEnabled}
                            questionError={questionError}
                            settings={{ showClose: true }}
                        />
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            questionText={
                                DEFAULT_TEXT.QUESTION.TEXT_HIGHLIGHTER
                                    .ALTERNATIVE_QUESTION_LABEL
                            }
                            settings={{ showTextarea: true, noBorder: true }}
                            disabled={!unlockSaveAndMedia}
                            questionError={questionError}
                            defaultTextType={MULTIMEDIA_TYPES.TEXT}
                            keyName="alternateWordingTexts"
                            disableAddBubble
                        />
                        <Notes
                            note={`Based on the number of words, the content will be shown to participants in font size ${dynamicFontSize}px.`}
                        />
                        <div className="option-settings-block">
                            <div className="row">
                                <label className="col-md-12 form-group-header">
                                    Option types
                                </label>
                                <div className="col-md-12 form-group">
                                    <CheckboxMultiSelect
                                        options={DEFAULT_TRANSLATION_BUTTONS(
                                            QTYPES.TEXT_HIGHLIGHTER,
                                            this.props.t,
                                            this.props.currentProject.languages.map(
                                                (l) => l.value
                                            )
                                        )}
                                        value={question.buttons || []}
                                        onChangeCallback={(response) =>
                                            this.setQuestion(
                                                'buttons',
                                                response
                                            )
                                        }
                                        disabled={!editEnabled}
                                    />
                                </div>
                            </div>
                            <div className="form-group-header display-flex">
                                Options
                                <CollaspeButton
                                    isOptionExpand={this.state.isOptionExpand}
                                    toggleOptionExpand={this.toggleOptionExpand}
                                />
                            </div>

                            {(questionError &&
                                (questionError.buttons || {}).isError && (
                                    <div
                                        className={`form-group ${
                                            questionError ? 'error' : ''
                                        }`}
                                    >
                                        <label className="form-group__label form-group__label--block">
                                            {questionError.buttons
                                                ?.emptyButtons ||
                                                alertMessage.BUTTON_ERROR}
                                        </label>
                                    </div>
                                )) ||
                                null}
                            {/** Show Option Cards according to option type */}
                            {isOptionExpand &&
                                this.renderOptionCard(
                                    OPTION_TYPES.TEXT_ONLY,
                                    {
                                        disableLabel: false,
                                        disableValue: true,
                                        disableRemove: true,
                                        disableDrag: true
                                    },
                                    'buttons'
                                )}
                        </div>
                        <div className="option-settings-block">
                            <div className="form-group-header">
                                Number of areas to select
                            </div>

                            <div className="row">
                                <div
                                    className={classnames(
                                        'col-md-6 form-group',
                                        {
                                            error:
                                                questionError &&
                                                questionError.min
                                        }
                                    )}
                                >
                                    <input
                                        type="number" //Keep validation here
                                        className="form-group__control opt-min"
                                        placeholder="Minimum number of area selects"
                                        onChange={(event) =>
                                            this.setQuestion(
                                                'min',
                                                parseInt(event.target.value)
                                            )
                                        }
                                        value={
                                            !question.min ? null : question.min
                                        }
                                        name="min"
                                        min="0"
                                        disabled={!editEnabled}
                                    />
                                    {questionError && questionError.min && (
                                        <div
                                            className={`form-group ${
                                                questionError ? 'error' : ''
                                            }`}
                                            style={{ margin: 0 }}
                                        >
                                            <label className="form-group__label form-group__label--block">
                                                {questionError.min}
                                            </label>
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={classnames(
                                        'col-md-6 form-group',
                                        {
                                            error:
                                                questionError &&
                                                questionError.max
                                        }
                                    )}
                                >
                                    <input
                                        type="number" //Keep validation here
                                        className="form-group__control opt-max"
                                        placeholder="Maximum number of area selects"
                                        onChange={(event) =>
                                            this.setQuestion(
                                                'max',
                                                parseInt(event.target.value)
                                            )
                                        }
                                        value={
                                            !question.max ? null : question.max
                                        }
                                        name="max"
                                        min="0"
                                        disabled={!editEnabled}
                                    />
                                    {questionError && questionError.max && (
                                        <div
                                            className={`form-group ${
                                                questionError ? 'error' : ''
                                            }`}
                                            style={{ margin: 0 }}
                                        >
                                            <label className="form-group__label form-group__label--block">
                                                {questionError.max}
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="option-settings-block">
                            <MultimediaBubble
                                languages={currentProject.languages}
                                question={question}
                                setValue={this.setQuestion}
                                setValueWithLanguage={
                                    this.NEW_setQuestionTextTranslation
                                }
                                questionError={questionError}
                                keyName="continueTexts"
                                disabled={!this.props.editEnabled}
                                disableAddBubble
                                defaultTextType={MULTIMEDIA_TYPES.TEXT}
                                questionText="Continue button text"
                                defaultText="BUTTON.CONTINUE"
                                settings={{
                                    noBorder: true,
                                    useTranslation: true
                                }}
                            />
                        </div>
                    </div>
                    {/*end of builder body*/}
                </div>
            </div>
        );
    }
}

export default NotifyHOC(
    compose(
        withTranslation(),
        connect(
            mapStateToQuestionFormBaseProps,
            mapDispatchToQuestionFormBaseProps
        ),
        withRouter
    )(TextHighlighter)
);
